import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import './DraftingAutomation.css';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Slider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  AddRounded,
  AutoAwesomeRounded,
  CheckCircleRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
  CloseRounded,
  CloudDoneRounded,
  CloudSyncRounded,
  CodeRounded,
  DownloadRounded,
  FindInPageRounded,
  FormatAlignCenterRounded,
  FormatAlignJustifyRounded,
  FormatAlignLeftRounded,
  FormatAlignRightRounded,
  FormatBoldRounded,
  FormatIndentDecreaseRounded,
  FormatIndentIncreaseRounded,
  FormatItalicRounded,
  FormatListBulletedRounded,
  FormatListNumberedRounded,
  FormatClearRounded,
  FormatQuoteRounded,
  FormatStrikethroughRounded,
  FormatUnderlinedRounded,
  HistoryRounded,
  LinkRounded,
  MenuBookRounded,
  MoreHorizRounded,
  PrintRounded,
  RedoRounded,
  SaveRounded,
  SearchRounded,
  UndoRounded,
  VerifiedRounded,
  VisibilityRounded,
  WarningAmberRounded,
} from '@mui/icons-material';

const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');
const api = path => `${API_BASE}${path}`;

const DEFAULT_CHECKLIST = {
  factsVerified: false,
  authoritiesVerified: false,
  quotationsVerified: false,
  formattingReviewed: false,
  serviceFilingReviewed: false,
  clientInstructionsConfirmed: false,
  seniorReviewComplete: false,
};

function escapeHtml(value = '') {
  return String(value).replace(/[&<>"']/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;',
  }[char]));
}

function courtIdFor(courts = [], value = '') {
  const normalized = String(value).trim().toLowerCase();
  return courts.find(court => [court.id, court.name, ...(court.aliases || [])].some(name => String(name).toLowerCase() === normalized))?.id || '';
}

function jurisdictionFor(value = '') {
  const normalized = String(value).trim().toLowerCase().replace(/[^a-z]/g, '');
  if (normalized === 'on' || normalized === 'ontario') return 'Ontario';
  if (normalized === 'bc' || normalized === 'britishcolumbia') return 'British Columbia';
  return String(value).trim();
}

function initialDocumentHtml({ template, matter, metadata }) {
  const documentType = template?.title || 'Controlled legal document';
  const sections = template?.sections?.map(section => ({ section })) || metadata?.templateSections?.[documentType] || [
    { section: 'Overview' }, { section: 'Facts' }, { section: 'Analysis' }, { section: 'Relief requested' },
  ];
  const caption = matter?.clientName || matter?.title || 'BETWEEN';
  const fileNumber = matter?.fileNumber || '[INFORMATION / COURT FILE NUMBER]';
  const jurisdiction = matter?.jurisdiction || '[JURISDICTION]';
  const sectionHtml = sections.flatMap((section, index) => [
    `<h2>${template?.filingStatus === 'internal' ? '' : `PART ${toRoman(index + 1)} — `}${escapeHtml(section.section).toUpperCase()}</h2>`,
    `<p>[Draft ${escapeHtml(section.section.toLowerCase())}. Every material fact requires a record source; every legal proposition requires a verified authority and pinpoint.]</p>`,
  ]);
  if (template?.filingStatus === 'internal') {
    return [
      '<p><strong>CONFIDENTIAL LEGAL MATTER WORK PRODUCT</strong></p>',
      `<h1>${escapeHtml(documentType)}</h1>`,
      `<p><strong>Matter:</strong> ${escapeHtml(matter?.title || caption)}<br><strong>Client:</strong> ${escapeHtml(caption)}<br><strong>Matter no.:</strong> ${escapeHtml(matter?.matterNumber || '[MATTER NUMBER]')}<br><strong>Jurisdiction / court:</strong> ${escapeHtml(jurisdiction)} · ${escapeHtml(matter?.court || '[COURT]')}</p>`,
      '<p><strong>Prepared by:</strong> [AUTHOR]<br><strong>Date:</strong> [DATE]<br><strong>Review status:</strong> Working draft</p>',
      ...sectionHtml,
      '<p><strong>Professional review:</strong> [REVIEWER / DATE / INSTRUCTIONS]</p>',
    ].join('');
  }
  if (template?.filingStatus === 'served') {
    return [
      '<p>[FIRM LETTERHEAD]</p>',
      '<p>[DATE]</p>',
      '<p>BY [SERVICE METHOD]</p>',
      '<p>[RECIPIENT NAME]<br>[RECIPIENT ADDRESS / EMAIL]</p>',
      `<p><strong>Re: ${escapeHtml(matter?.title || caption)} · ${escapeHtml(fileNumber)}</strong></p>`,
      ...sectionHtml,
      '<p>Yours truly,</p>',
      '<p>[RESPONSIBLE PROFESSIONAL NAME]<br>[FIRM / CONTACT INFORMATION]</p>',
      '<p>Encl. [LIST]<br>cc: [CLIENT / OTHER AUTHORIZED RECIPIENT]</p>',
    ].join('');
  }
  const officialNotice = template?.kind === 'official-form'
    ? `<p><strong>OFFICIAL FORM CONTROL:</strong> Use the current court-issued ${escapeHtml(template.officialFormNumber || 'form')} from ${escapeHtml(template.source?.title || 'the official court source')} before filing. This workspace does not replace the prescribed form.</p>`
    : '';
  return [
    `<p class="ql-align-center"><strong>${escapeHtml(jurisdiction).toUpperCase()}</strong></p>`,
    `<p class="ql-align-center"><strong>${escapeHtml(matter?.court || '[COURT]')}</strong></p>`,
    `<p class="ql-align-right">Court File No. ${escapeHtml(fileNumber)}</p>`,
    `<h1 class="ql-align-center">${escapeHtml(documentType).toUpperCase()}</h1>`,
    `<p class="ql-align-center"><strong>${escapeHtml(caption)}</strong></p>`,
    '<p><br></p>',
    officialNotice,
    ...sectionHtml,
    '<p><br></p>',
    '<p>ALL OF WHICH IS RESPECTFULLY SUBMITTED.</p>',
    '<p>Dated: [DATE]</p>',
    '<p>[RESPONSIBLE PROFESSIONAL NAME]<br>[FIRM / CONTACT INFORMATION]<br>Representative for [PARTY]</p>',
  ].join('');
}

function toRoman(number) {
  const values = [['X', 10], ['IX', 9], ['V', 5], ['IV', 4], ['I', 1]];
  let remaining = number;
  return values.reduce((out, [symbol, value]) => {
    while (remaining >= value) { out += symbol; remaining -= value; }
    return out;
  }, '');
}

function wordCountFromQuill(quill) {
  const text = quill?.getText()?.trim() || '';
  return text ? text.split(/\s+/).length : 0;
}

export function findTextRanges(text = '', query = '') {
  const needle = String(query).trim().toLowerCase();
  const haystack = String(text).toLowerCase();
  if (!needle || !haystack) return [];
  const matches = [];
  let index = 0;
  while ((index = haystack.indexOf(needle, index)) !== -1) {
    matches.push({ index, length: needle.length });
    index += Math.max(1, needle.length);
  }
  return matches;
}

function downloadHtml(document) {
  if (!document) return;
  const full = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(document.title)}</title><style>body{font-family:Arial,sans-serif;font-size:12pt;line-height:2;max-width:7in;margin:.8in auto}h1{text-align:center;font-size:16pt}h2{font-size:12pt;margin-top:24pt}p{margin:0 0 10pt}</style></head><body>${document.contentHtml || ''}</body></html>`;
  const blob = new Blob([full], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = window.document.createElement('a');
  anchor.href = url;
  anchor.download = `${document.title.replace(/[^a-z0-9]+/gi, '_') || 'legal_document'}.html`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function FormatButton({ title, active = false, disabled = false, onClick, children }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          onMouseDown={event => event.preventDefault()}
          onClick={onClick}
          sx={{ borderRadius: 1, bgcolor: active ? 'rgba(38,113,105,.14)' : 'transparent', color: active ? 'primary.main' : 'text.secondary' }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function Resizer({ side, onResize }) {
  const begin = event => {
    event.preventDefault();
    let previous = event.clientX;
    const move = moveEvent => {
      const delta = side === 'left' ? moveEvent.clientX - previous : previous - moveEvent.clientX;
      previous = moveEvent.clientX;
      onResize(delta);
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };
  return <Box className="draft-resizer" onMouseDown={begin} role="separator" aria-orientation="vertical" />;
}

export default function DraftingAutomation() {
  const [searchParams, setSearchParams] = useSearchParams();
  const compactLayout = useMediaQuery('(max-width:1199px)');
  const editorHostRef = useRef(null);
  const editorCanvasRef = useRef(null);
  const quillRef = useRef(null);
  const savedRangeRef = useRef(null);
  const initialLoadRef = useRef(false);
  const [metadata, setMetadata] = useState(null);
  const [matters, setMatters] = useState([]);
  const [matter, setMatter] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saveState, setSaveState] = useState('saved');
  const [dirtyHtml, setDirtyHtml] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [selectionText, setSelectionText] = useState('');
  const [format, setFormat] = useState({});
  const [outline, setOutline] = useState([]);
  const [leftOpen, setLeftOpen] = useState(() => window.matchMedia?.('(min-width:1200px)').matches ?? true);
  const [rightOpen, setRightOpen] = useState(() => window.matchMedia?.('(min-width:1200px)').matches ?? true);
  const [leftWidth, setLeftWidth] = useState(272);
  const [rightWidth, setRightWidth] = useState(342);
  const [rightTab, setRightTab] = useState(0);
  const [leftTab, setLeftTab] = useState(0);
  const [documentSearch, setDocumentSearch] = useState('');
  const [ribbonTab, setRibbonTab] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [findOpen, setFindOpen] = useState(false);
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [activeFindIndex, setActiveFindIndex] = useState(-1);
  const [newDialog, setNewDialog] = useState(false);
  const [newStage, setNewStage] = useState('intake-retainer');
  const [newProceedingType, setNewProceedingType] = useState('');
  const [newKind, setNewKind] = useState('');
  const [newQuery, setNewQuery] = useState('');
  const [newTemplateId, setNewTemplateId] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [aiInstruction, setAiInstruction] = useState('Improve clarity and precision without changing the legal meaning.');
  const [aiConsent, setAiConsent] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [authority, setAuthority] = useState({ title: '', citation: '', pinpoint: '', url: '', proposition: '', treatment: '' });
  const [checkpointDialog, setCheckpointDialog] = useState(false);
  const [checkpointLabel, setCheckpointLabel] = useState('');
  const [versionPreview, setVersionPreview] = useState(null);

  const qualityChecks = metadata?.qualityChecks || [];
  const selectedMatterId = searchParams.get('matterId') || searchParams.get('id') || '';
  const selectedDocumentId = searchParams.get('documentId') || '';
  const requestedStageId = searchParams.get('stage') || '';
  const matterJurisdiction = jurisdictionFor(matter?.jurisdiction);
  const matterCourtId = courtIdFor(metadata?.courts, matter?.court);
  const filteredDocuments = useMemo(() => {
    const query = documentSearch.trim().toLowerCase();
    if (!query) return documents;
    return documents.filter(item => `${item.title} ${item.documentType} ${item.status}`.toLowerCase().includes(query));
  }, [documentSearch, documents]);
  const findMatches = useMemo(() => {
    void dirtyHtml; // Recompute indexes whenever the live Quill document changes.
    return findTextRanges(quillRef.current?.getText() || '', findText);
  }, [dirtyHtml, findText]);
  const selectionWordCount = selectionText.trim() ? selectionText.trim().split(/\s+/).length : 0;
  const catalogTemplates = useMemo(() => {
    const query = newQuery.trim().toLowerCase();
    return (metadata?.documentCatalog || []).filter(template => {
      if (matterJurisdiction && !template.jurisdictions.includes(matterJurisdiction)) return false;
      if (matterCourtId && !template.courts.includes(matterCourtId)) return false;
      if (newStage && template.stageId !== newStage) return false;
      if (newProceedingType && !template.proceedingTypes.includes(newProceedingType)) return false;
      if (newKind && template.kind !== newKind) return false;
      if (query && !`${template.title} ${template.category} ${template.description} ${template.officialFormNumber || ''}`.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [matterCourtId, matterJurisdiction, metadata?.documentCatalog, newKind, newProceedingType, newQuery, newStage]);
  const newTemplate = (metadata?.documentCatalog || []).find(template => template.id === newTemplateId) || null;

  useEffect(() => {
    if (!newDialog) return;
    if (!catalogTemplates.some(template => template.id === newTemplateId)) {
      setNewTemplateId(catalogTemplates[0]?.id || '');
    }
  }, [catalogTemplates, newDialog, newTemplateId]);

  useEffect(() => {
    if (!compactLayout) return;
    setLeftOpen(false);
    setRightOpen(false);
  }, [compactLayout]);

  const request = useCallback(async (path, options = {}) => {
    const response = await fetch(api(path), {
      ...options,
      headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || `Request failed (${response.status})`);
    return body;
  }, []);

  const refreshOutline = useCallback(() => {
    const root = quillRef.current?.root;
    if (!root) return;
    setOutline([...root.querySelectorAll('h1,h2,h3')].map((node, index) => ({
      id: `heading-${index}`,
      level: Number(node.tagName.slice(1)),
      text: node.textContent || `Heading ${index + 1}`,
      node,
    })));
  }, []);

  useEffect(() => {
    Promise.all([
      request('/api/drafting/metadata'),
      request('/api/matters'),
    ]).then(([meta, matterData]) => {
      setMetadata(meta);
      setMatters(matterData.matters || []);
      const chosen = (matterData.matters || []).find(item => item.id === selectedMatterId) || matterData.matters?.[0] || null;
      setMatter(chosen);
      if (chosen && chosen.id !== selectedMatterId) setSearchParams({ matterId: chosen.id }, { replace: true });
    }).catch(err => setError(err.message)).finally(() => setLoading(false));
  }, [request]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDocuments = useCallback(async (matterId, preferredId = '') => {
    if (!matterId) { setDocuments([]); setDocument(null); return; }
    try {
      const list = await request(`/api/drafting/documents?matterId=${encodeURIComponent(matterId)}`);
      setDocuments(list.documents || []);
      const id = preferredId || list.documents?.[0]?.id;
      if (!id) { setDocument(null); return; }
      const detail = await request(`/api/drafting/documents/${id}`);
      setDocument(detail.document);
      setSearchParams({ matterId, documentId: detail.document.id }, { replace: true });
    } catch (err) {
      setError(err.message);
    }
  }, [request, setSearchParams]);

  useEffect(() => {
    if (matter?.id) loadDocuments(matter.id, selectedDocumentId);
  }, [matter?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!editorHostRef.current || quillRef.current) return;
    const quill = new Quill(editorHostRef.current, {
      theme: 'snow',
      placeholder: 'Start drafting…',
      modules: { toolbar: false, history: { delay: 700, maxStack: 200, userOnly: true } },
      formats: ['header', 'font', 'size', 'bold', 'italic', 'underline', 'strike', 'color', 'background', 'align', 'list', 'indent', 'blockquote', 'link', 'code-block'],
    });
    quillRef.current = quill;
    quill.on('selection-change', range => {
      if (range) {
        savedRangeRef.current = range;
        setSelectionText(range.length ? quill.getText(range.index, range.length) : '');
        setFormat(quill.getFormat(range));
      }
    });
    quill.on('text-change', (_delta, _old, source) => {
      if (source === 'silent') return;
      setDirtyHtml(quill.root.innerHTML);
      setWordCount(wordCountFromQuill(quill));
      setSaveState('unsaved');
      window.clearTimeout(quill.__outlineTimer);
      quill.__outlineTimer = window.setTimeout(refreshOutline, 250);
    });
    return () => {
      window.clearTimeout(quill.__outlineTimer);
      quillRef.current = null;
    };
  }, [refreshOutline, document?.id]);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill || !document) return;
    initialLoadRef.current = true;
    quill.clipboard.dangerouslyPasteHTML(document.contentHtml || '<p><br></p>', 'silent');
    quill.history.clear();
    setDirtyHtml(document.contentHtml || '');
    setWordCount(wordCountFromQuill(quill));
    setSaveState('saved');
    setAiResult(null);
    setSelectionText('');
    setFindOpen(false);
    setFindText('');
    setReplaceText('');
    setActiveFindIndex(-1);
    window.setTimeout(() => { initialLoadRef.current = false; refreshOutline(); }, 0);
  }, [document?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const autosaveDocumentId = document?.id;
  useEffect(() => {
    if (!autosaveDocumentId || saveState !== 'unsaved' || initialLoadRef.current) return;
    const timer = window.setTimeout(async () => {
      setSaveState('saving');
      try {
        const body = await request(`/api/drafting/documents/${autosaveDocumentId}`, {
          method: 'PATCH', body: JSON.stringify({ contentHtml: dirtyHtml }),
        });
        setDocument(current => current?.id === body.document.id ? { ...body.document, versions: current.versions } : current);
        setSaveState('saved');
      } catch (err) {
        setSaveState('error');
        setError(err.message);
      }
    }, 1100);
    return () => window.clearTimeout(timer);
  }, [autosaveDocumentId, dirtyHtml, request, saveState]);

  const saveNow = useCallback(async (showNotice = true) => {
    const id = document?.id;
    const quill = quillRef.current;
    if (!id || !quill) return null;
    setSaveState('saving');
    try {
      const body = await request(`/api/drafting/documents/${id}`, {
        method: 'PATCH', body: JSON.stringify({ contentHtml: quill.root.innerHTML }),
      });
      setDocument(current => current?.id === body.document.id ? body.document : current);
      setDocuments(items => items.map(item => item.id === body.document.id ? { ...item, ...body.document, contentHtml: undefined } : item));
      setDirtyHtml(quill.root.innerHTML);
      setSaveState('saved');
      if (showNotice) setNotice('Live draft saved.');
      return body.document;
    } catch (err) {
      setSaveState('error');
      setError(err.message);
      return null;
    }
  }, [document?.id, request]);

  useEffect(() => {
    const handleShortcut = event => {
      if (!document || !(event.metaKey || event.ctrlKey)) return;
      const key = event.key.toLowerCase();
      if (key === 's') {
        event.preventDefault();
        saveNow();
      }
      if (key === 'f') {
        event.preventDefault();
        setFindOpen(true);
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [document, saveNow]);

  const toggleNavigator = () => {
    setLeftOpen(value => !value);
    if (compactLayout) setRightOpen(false);
  };

  const toggleInsights = () => {
    setRightOpen(value => !value);
    if (compactLayout) setLeftOpen(false);
  };

  const openInsights = tab => {
    setRightTab(tab);
    setRightOpen(true);
    if (compactLayout) setLeftOpen(false);
  };

  const selectFindMatch = (direction = 1) => {
    const quill = quillRef.current;
    if (!quill || !findMatches.length) {
      setActiveFindIndex(-1);
      return;
    }
    const nextIndex = activeFindIndex < 0
      ? (direction < 0 ? findMatches.length - 1 : 0)
      : (activeFindIndex + direction + findMatches.length) % findMatches.length;
    const match = findMatches[nextIndex];
    quill.setSelection(match.index, match.length, 'api');
    quill.scrollSelectionIntoView();
    setActiveFindIndex(nextIndex);
  };

  const replaceCurrentMatch = () => {
    const quill = quillRef.current;
    const match = findMatches[activeFindIndex];
    if (!quill || !match) { selectFindMatch(1); return; }
    quill.deleteText(match.index, match.length, 'user');
    quill.insertText(match.index, replaceText, 'user');
    quill.setSelection(match.index, replaceText.length, 'api');
    setActiveFindIndex(-1);
  };

  const replaceEveryMatch = () => {
    const quill = quillRef.current;
    if (!quill || !findMatches.length) return;
    [...findMatches].reverse().forEach(match => {
      quill.deleteText(match.index, match.length, 'user');
      quill.insertText(match.index, replaceText, 'user');
    });
    setActiveFindIndex(-1);
    setNotice(`${findMatches.length} occurrence${findMatches.length === 1 ? '' : 's'} replaced. Use Undo to revert.`);
  };

  const setActiveMatter = value => {
    const next = matters.find(item => item.id === value) || null;
    setMatter(next);
    setDocument(null);
    if (next) setSearchParams({ matterId: next.id });
  };

  const openDocument = async id => {
    try {
      const body = await request(`/api/drafting/documents/${id}`);
      setDocument(body.document);
      setSearchParams({ matterId: matter.id, documentId: id });
    } catch (err) { setError(err.message); }
  };

  const openNewDocumentDialog = () => {
    const supportedStage = (metadata?.lifecycleStages || []).some(stage => stage.id === requestedStageId);
    setNewStage(supportedStage ? requestedStageId : (matter?.stage || 'intake-retainer'));
    setNewProceedingType('');
    setNewKind('');
    setNewQuery('');
    setNewTemplateId('');
    setNewTitle('');
    setNewDialog(true);
  };

  const createDocument = async () => {
    if (!matter || !newTemplate) return;
    try {
      const title = newTitle.trim() || `${newTemplate.title} — ${matter.clientName || matter.title}`;
      const standard = (metadata?.standards || []).find(item => item.courtIds?.includes(matterCourtId));
      const body = await request('/api/drafting/documents', {
        method: 'POST',
        body: JSON.stringify({
          matterId: matter.id,
          templateId: newTemplate.id,
          title,
          documentType: newTemplate.title,
          jurisdiction: matter.jurisdiction || 'Ontario',
          court: matter.court || 'Ontario Court of Justice',
          courtFileNumber: matter.fileNumber || '',
          contentHtml: initialDocumentHtml({ template: newTemplate, matter, metadata }),
          checklist: DEFAULT_CHECKLIST,
          metadata: {
            templateId: newTemplate.id,
            lifecycleStageId: newTemplate.stageId,
            proceedingType: newProceedingType || null,
            standardId: standard?.id || null,
            paragraphNumbering: true,
            lineSpacing: matterCourtId === 'bc-ca' ? 1.5 : 2,
            fontSizePt: 12,
          },
        }),
      });
      setNewDialog(false);
      setNewTitle('');
      await loadDocuments(matter.id, body.document.id);
      setNotice('Document created with an immutable initial version.');
    } catch (err) { setError(err.message); }
  };

  const updateDocument = async changes => {
    if (!document) return;
    try {
      const body = await request(`/api/drafting/documents/${document.id}`, {
        method: 'PATCH', body: JSON.stringify(changes),
      });
      setDocument(body.document);
      setDocuments(items => items.map(item => item.id === body.document.id ? { ...item, ...body.document, contentHtml: undefined } : item));
      setNotice('Document controls updated.');
    } catch (err) { setError(err.message); }
  };

  const createVersion = () => {
    if (!document) return;
    setCheckpointLabel(`Review checkpoint ${document.versionNumber + 1}`);
    setCheckpointDialog(true);
  };

  const saveCheckpoint = async () => {
    const label = checkpointLabel.trim();
    if (!document || !label) return;
    try {
      if (saveState !== 'saved') {
        const saved = await saveNow(false);
        if (!saved) return;
      }
      const body = await request(`/api/drafting/documents/${document.id}/versions`, {
        method: 'POST', body: JSON.stringify({ label }),
      });
      setDocument(body.document);
      setSaveState('saved');
      setCheckpointDialog(false);
      setNotice(`Version ${body.document.versionNumber} saved.`);
    } catch (err) { setError(err.message); }
  };

  const restoreVersion = async version => {
    if (!window.confirm(`Restore version ${version.versionNumber} as a new working version? Current history will remain intact.`)) return;
    try {
      const body = await request(`/api/drafting/documents/${document.id}/versions/${version.id}/restore`, { method: 'POST' });
      setDocument(body.document);
      const quill = quillRef.current;
      if (quill) {
        quill.clipboard.dangerouslyPasteHTML(body.document.contentHtml || '<p><br></p>', 'silent');
        quill.history.clear();
        setDirtyHtml(body.document.contentHtml || '');
        setWordCount(wordCountFromQuill(quill));
        setSaveState('saved');
        window.setTimeout(refreshOutline, 0);
      }
      setVersionPreview(null);
      setNotice(`Version ${version.versionNumber} restored as version ${body.document.versionNumber}.`);
    } catch (err) { setError(err.message); }
  };

  const applyFormat = (name, value = true) => {
    const quill = quillRef.current;
    if (!quill) return;
    const range = savedRangeRef.current || quill.getSelection() || { index: 0, length: 0 };
    quill.setSelection(range);
    quill.format(name, value, 'user');
    setFormat(quill.getFormat(range));
  };

  const promptLink = () => {
    const url = window.prompt('Paste the verified URL or pinpoint link');
    if (url) applyFormat('link', url);
  };

  const runAi = async (mode = 'rewrite') => {
    const range = savedRangeRef.current;
    const quill = quillRef.current;
    if (!range?.length || !quill) { setError('Select the exact text you want the AI to revise.'); return; }
    if (!aiConsent) { setError('Confirm confidential AI processing in the AI pane first.'); return; }
    setAiBusy(true);
    setAiResult(null);
    try {
      const body = await request('/api/drafting/assist', {
        method: 'POST',
        body: JSON.stringify({
          documentId: document.id,
          selection: quill.getText(range.index, range.length),
          surroundingText: quill.getText(Math.max(0, range.index - 4000), Math.min(quill.getLength(), range.length + 8000)),
          instruction: aiInstruction,
          mode,
          confirmConfidentialProcessing: true,
        }),
      });
      setAiResult({ ...body, range });
    } catch (err) { setError(err.message); }
    finally { setAiBusy(false); }
  };

  const acceptAi = () => {
    const quill = quillRef.current;
    if (!quill || !aiResult?.range) return;
    const { index, length } = aiResult.range;
    const inherited = quill.getFormat(index, Math.max(1, length));
    quill.deleteText(index, length, 'user');
    quill.insertText(index, aiResult.replacementText, inherited, 'user');
    quill.setSelection(index, aiResult.replacementText.length);
    setAiResult(null);
    setNotice('AI revision applied. Review and verify before advancing status.');
  };

  const addAuthority = async () => {
    if (!authority.citation.trim() && !authority.title.trim()) { setError('Add a case name or citation.'); return; }
    const next = [...(document.authorities || []), { ...authority, verified: false }];
    await updateDocument({ authorities: next, checklist: { ...document.checklist, authoritiesVerified: false, quotationsVerified: false } });
    setAuthority({ title: '', citation: '', pinpoint: '', url: '', proposition: '', treatment: '' });
  };

  const toggleAuthority = async (index, verified) => {
    const next = (document.authorities || []).map((item, itemIndex) => itemIndex === index ? { ...item, verified } : item);
    await updateDocument({ authorities: next, checklist: { ...document.checklist, authoritiesVerified: next.length > 0 && next.every(item => item.verified) } });
  };

  const qualityDone = qualityChecks.filter(check => document?.checklist?.[check.key]).length;
  const readiness = qualityChecks.length ? Math.round((qualityDone / qualityChecks.length) * 100) : 0;
  const placeholderCount = (quillRef.current?.getText()?.match(/\[(?:FACT|AUTHORITY|PINPOINT|DATE|RESPONSIBLE PROFESSIONAL|PARTY|INFORMATION|Draft)[^\]]*\]/gi) || []).length;
  const activeCourtId = courtIdFor(metadata?.courts, document?.court || matter?.court);
  const standard = (metadata?.standards || []).find(item => item.courtIds?.includes(activeCourtId))
    || metadata?.standards?.[0];

  const setPanelWidth = (side, delta) => {
    if (side === 'left') setLeftWidth(value => Math.max(220, Math.min(460, value + delta)));
    else setRightWidth(value => Math.max(292, Math.min(520, value + delta)));
  };

  const fitPageToCanvas = useCallback(() => {
    const availableWidth = (editorCanvasRef.current?.clientWidth || 816) - (compactLayout ? 28 : 72);
    const fitted = Math.max(50, Math.min(100, Math.floor((availableWidth / 816) * 20) * 5));
    setZoom(fitted);
  }, [compactLayout]);

  useEffect(() => {
    if (!compactLayout || loading || !document?.id) return;
    window.setTimeout(fitPageToCanvas, 0);
  }, [compactLayout, document?.id, fitPageToCanvas, loading]);

  if (loading) return <Box sx={{ height: '60vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;

  return (
    <Box className="draft-workspace">
      <Box className="draft-commandbar">
        <Stack direction="row" alignItems="center" spacing={1.2} sx={{ minWidth: 0 }}>
          <Typography className="draft-brand" sx={{ fontWeight: 800, color: '#fff', whiteSpace: 'nowrap' }}>Drafting Studio</Typography>
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,.18)' }} />
          <TextField
            select size="small" value={matter?.id || ''} onChange={event => setActiveMatter(event.target.value)}
            inputProps={{ 'aria-label': 'Select drafting matter' }}
            className="draft-matter-picker"
            sx={{ minWidth: 220, '& .MuiInputBase-root': { height: 34, bgcolor: 'rgba(255,255,255,.1)', color: '#fff' }, '& fieldset': { borderColor: 'rgba(255,255,255,.22)' }, '& svg': { color: '#fff' } }}
          >
            {matters.map(item => <MenuItem key={item.id} value={item.id}>{item.matterNumber} · {item.clientName || item.title}</MenuItem>)}
          </TextField>
          {document && (
            <TextField
              value={document.title}
              onChange={event => setDocument(current => ({ ...current, title: event.target.value }))}
              onBlur={event => updateDocument({ title: event.target.value })}
              inputProps={{ 'aria-label': 'Document title' }}
              className="draft-title-input"
              variant="standard"
              sx={{ minWidth: 280, '& input': { color: '#fff', fontWeight: 650 }, '&:before': { borderColor: 'transparent' } }}
            />
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={.6}>
          {saveState === 'saving' ? <Chip icon={<CloudSyncRounded />} label="Saving…" size="small" /> :
            saveState === 'error' ? <Chip icon={<WarningAmberRounded />} label="Save failed" color="error" size="small" /> :
              saveState === 'unsaved' ? <Chip icon={<WarningAmberRounded />} label="Unsaved" size="small" color="warning" /> :
                <Chip icon={<CloudDoneRounded />} label="Saved" size="small" sx={{ bgcolor: 'rgba(255,255,255,.12)', color: '#fff' }} />}
          <Button className="draft-save-button" size="small" color="inherit" startIcon={<SaveRounded />} onClick={() => saveNow()} disabled={!document || saveState === 'saving'}>Save</Button>
          <Tooltip title="Toggle document navigator"><IconButton color="inherit" onClick={toggleNavigator} aria-label="Toggle document navigator">{leftOpen ? <ChevronLeftRounded /> : <ChevronRightRounded />}</IconButton></Tooltip>
          <Tooltip title="Toggle drafting insights"><IconButton color="inherit" onClick={toggleInsights} aria-label="Toggle drafting insights">{rightOpen ? <ChevronRightRounded /> : <ChevronLeftRounded />}</IconButton></Tooltip>
        </Stack>
      </Box>

      <Paper square elevation={0} className="draft-ribbon">
        <Tabs value={ribbonTab} onChange={(_, value) => setRibbonTab(value)} sx={{ minHeight: 32 }}>
          <Tab label="Home" sx={{ minHeight: 32, py: 0 }} />
          <Tab label="Insert" sx={{ minHeight: 32, py: 0 }} />
          <Tab label="Review" sx={{ minHeight: 32, py: 0 }} />
          <Tab label="View" sx={{ minHeight: 32, py: 0 }} />
        </Tabs>
        <Divider />
        <Box className="draft-ribbon-tools">
          {ribbonTab === 0 && <>
            <Stack direction="row" spacing={.25} alignItems="center">
              <FormatButton title="Undo" disabled={!document} onClick={() => quillRef.current?.history.undo()}><UndoRounded fontSize="small" /></FormatButton>
              <FormatButton title="Redo" disabled={!document} onClick={() => quillRef.current?.history.redo()}><RedoRounded fontSize="small" /></FormatButton>
            </Stack>
            <Divider orientation="vertical" flexItem />
            <Button size="small" startIcon={<FindInPageRounded />} onClick={() => setFindOpen(value => !value)} disabled={!document}>Find</Button>
            <Divider orientation="vertical" flexItem />
            <TextField select size="small" disabled={!document} value={format.header || 0} onChange={event => applyFormat('header', Number(event.target.value) || false)} sx={{ width: 138 }}>
              <MenuItem value={0}>Normal text</MenuItem><MenuItem value={1}>Title</MenuItem><MenuItem value={2}>Heading 1</MenuItem><MenuItem value={3}>Heading 2</MenuItem>
            </TextField>
            <TextField select size="small" disabled={!document} value={format.font || 'default'} onChange={event => applyFormat('font', event.target.value === 'default' ? false : event.target.value)} sx={{ width: 116 }}>
              <MenuItem value="default">Arial</MenuItem><MenuItem value="serif">Times New Roman</MenuItem><MenuItem value="monospace">Monospace</MenuItem>
            </TextField>
            <TextField select size="small" disabled={!document} value={format.size || 'normal'} onChange={event => applyFormat('size', event.target.value === 'normal' ? false : event.target.value)} sx={{ width: 82 }}>
              <MenuItem value="small">10 pt</MenuItem><MenuItem value="normal">12 pt</MenuItem><MenuItem value="large">14 pt</MenuItem><MenuItem value="huge">18 pt</MenuItem>
            </TextField>
            <Stack direction="row" spacing={.15}>
              <FormatButton title="Bold" disabled={!document} active={format.bold} onClick={() => applyFormat('bold', !format.bold)}><FormatBoldRounded fontSize="small" /></FormatButton>
              <FormatButton title="Italic" disabled={!document} active={format.italic} onClick={() => applyFormat('italic', !format.italic)}><FormatItalicRounded fontSize="small" /></FormatButton>
              <FormatButton title="Underline" disabled={!document} active={format.underline} onClick={() => applyFormat('underline', !format.underline)}><FormatUnderlinedRounded fontSize="small" /></FormatButton>
              <FormatButton title="Strikethrough" disabled={!document} active={format.strike} onClick={() => applyFormat('strike', !format.strike)}><FormatStrikethroughRounded fontSize="small" /></FormatButton>
              <FormatButton title="Block quotation" disabled={!document} active={format.blockquote} onClick={() => applyFormat('blockquote', !format.blockquote)}><FormatQuoteRounded fontSize="small" /></FormatButton>
              <FormatButton title="Clear formatting" disabled={!document} onClick={() => quillRef.current?.removeFormat(savedRangeRef.current?.index || 0, savedRangeRef.current?.length || 0, 'user')}><FormatClearRounded fontSize="small" /></FormatButton>
            </Stack>
            <Divider orientation="vertical" flexItem />
            <Stack direction="row" spacing={.1}>
              {[['', FormatAlignLeftRounded], ['center', FormatAlignCenterRounded], ['right', FormatAlignRightRounded], ['justify', FormatAlignJustifyRounded]].map(([value, Icon]) => (
                <FormatButton key={value || 'left'} title={`Align ${value || 'left'}`} disabled={!document} active={(format.align || '') === value} onClick={() => applyFormat('align', value || false)}><Icon fontSize="small" /></FormatButton>
              ))}
              <FormatButton title="Bulleted list" disabled={!document} active={format.list === 'bullet'} onClick={() => applyFormat('list', format.list === 'bullet' ? false : 'bullet')}><FormatListBulletedRounded fontSize="small" /></FormatButton>
              <FormatButton title="Numbered list" disabled={!document} active={format.list === 'ordered'} onClick={() => applyFormat('list', format.list === 'ordered' ? false : 'ordered')}><FormatListNumberedRounded fontSize="small" /></FormatButton>
              <FormatButton title="Decrease indent" disabled={!document} onClick={() => applyFormat('indent', '-1')}><FormatIndentDecreaseRounded fontSize="small" /></FormatButton>
              <FormatButton title="Increase indent" disabled={!document} onClick={() => applyFormat('indent', '+1')}><FormatIndentIncreaseRounded fontSize="small" /></FormatButton>
            </Stack>
          </>}
          {ribbonTab === 1 && <>
            <Button size="small" startIcon={<LinkRounded />} onClick={promptLink}>Verified link</Button>
            <Button size="small" startIcon={<MenuBookRounded />} onClick={() => openInsights(2)}>Authority</Button>
            <Button size="small" startIcon={<CodeRounded />} onClick={() => {
              const range = savedRangeRef.current || { index: quillRef.current?.getLength() - 1 || 0, length: 0 };
              quillRef.current?.insertText(range.index + range.length, '\n[NEW SECTION]\n', { header: 2 }, 'user');
            }}>Section</Button>
            <Button size="small" startIcon={<MoreHorizRounded />} onClick={() => quillRef.current?.insertText(savedRangeRef.current?.index || 0, '\n────────────\n', 'user')}>Divider</Button>
          </>}
          {ribbonTab === 2 && <>
            <Button size="small" variant="contained" startIcon={<AutoAwesomeRounded />} onClick={() => openInsights(0)}>Revise selection</Button>
            <Button size="small" startIcon={<VerifiedRounded />} onClick={() => openInsights(1)}>Quality gate</Button>
            <Button size="small" startIcon={<HistoryRounded />} onClick={() => openInsights(3)}>Version history</Button>
            <Button size="small" startIcon={<SaveRounded />} onClick={createVersion}>Save version</Button>
          </>}
          {ribbonTab === 3 && <>
            <Typography variant="caption" sx={{ minWidth: 48 }}>Zoom</Typography>
            <Slider size="small" min={50} max={125} step={5} value={zoom} onChange={(_, value) => setZoom(value)} sx={{ width: 150 }} />
            <Typography variant="caption">{zoom}%</Typography>
            <Button size="small" onClick={fitPageToCanvas}>Fit page</Button>
            <Button size="small" onClick={() => setZoom(100)}>100%</Button>
            <Button size="small" startIcon={<DownloadRounded />} onClick={() => downloadHtml({ ...document, contentHtml: quillRef.current?.root.innerHTML || document?.contentHtml })}>HTML</Button>
            <Button size="small" startIcon={<PrintRounded />} onClick={() => window.print()}>Print / PDF</Button>
          </>}
        </Box>
      </Paper>

      {findOpen && document && (
        <Paper square elevation={2} className="draft-findbar" role="search" aria-label="Find and replace in document">
          <TextField
            size="small"
            value={findText}
            onChange={event => { setFindText(event.target.value); setActiveFindIndex(-1); }}
            onKeyDown={event => {
              if (event.key === 'Enter') selectFindMatch(event.shiftKey ? -1 : 1);
              if (event.key === 'Escape') setFindOpen(false);
            }}
            placeholder="Find in document"
            autoFocus
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment> }}
            inputProps={{ 'aria-label': 'Find in document' }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 58, textAlign: 'center' }}>{findMatches.length ? `${Math.max(1, activeFindIndex + 1)} / ${findMatches.length}` : '0 found'}</Typography>
          <IconButton size="small" aria-label="Previous match" onClick={() => selectFindMatch(-1)} disabled={!findMatches.length}><ChevronLeftRounded /></IconButton>
          <IconButton size="small" aria-label="Next match" onClick={() => selectFindMatch(1)} disabled={!findMatches.length}><ChevronRightRounded /></IconButton>
          <Divider orientation="vertical" flexItem />
          <TextField size="small" value={replaceText} onChange={event => setReplaceText(event.target.value)} placeholder="Replace with" inputProps={{ 'aria-label': 'Replace with' }} />
          <Button size="small" onClick={replaceCurrentMatch} disabled={!findMatches.length}>Replace</Button>
          <Button size="small" onClick={replaceEveryMatch} disabled={!findMatches.length}>Replace all</Button>
          <IconButton size="small" aria-label="Close find and replace" onClick={() => setFindOpen(false)}><CloseRounded /></IconButton>
        </Paper>
      )}

      <Box className="draft-main">
        {compactLayout && (leftOpen || rightOpen) && <Box className="draft-panel-backdrop" onClick={() => { setLeftOpen(false); setRightOpen(false); }} aria-hidden="true" />}
        {leftOpen && <>
          <Paper square elevation={0} className="draft-left-panel" sx={{ width: leftWidth }}>
            <Box sx={{ p: 1.5 }}>
              <Button fullWidth variant="contained" startIcon={<AddRounded />} onClick={openNewDocumentDialog} disabled={!matter}>New document</Button>
              {matter && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                {(metadata?.documentCatalog || []).filter(template => template.stageId === matter.stage && template.jurisdictions.includes(matterJurisdiction) && (!matterCourtId || template.courts.includes(matterCourtId))).length} current-stage templates · {metadata?.coverage?.totalTemplates || 0} catalogued
              </Typography>}
            </Box>
            <Tabs value={leftTab} onChange={(_, value) => setLeftTab(value)} variant="fullWidth" sx={{ minHeight: 38 }}>
              <Tab label={`Documents (${documents.length})`} sx={{ minHeight: 38, fontSize: 11 }} />
              <Tab label={`Outline (${outline.length})`} sx={{ minHeight: 38, fontSize: 11 }} />
            </Tabs>
            <Divider />
            <Box sx={{ px: 1, pb: 1.5, overflowY: 'auto', flex: 1 }}>
              {leftTab === 0 && <>
                <TextField
                  size="small"
                  fullWidth
                  value={documentSearch}
                  onChange={event => setDocumentSearch(event.target.value)}
                  placeholder="Find a draft"
                  sx={{ mt: 1.2, mb: 1 }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment> }}
                />
                <List dense disablePadding>
                  {filteredDocuments.map(item => (
                    <ListItemButton key={item.id} selected={document?.id === item.id} onClick={() => { openDocument(item.id); if (compactLayout) setLeftOpen(false); }} sx={{ borderRadius: 1.5, mb: .55, alignItems: 'flex-start' }}>
                      <ListItemText
                        primary={item.title}
                        secondary={<><span>{item.documentType} · v{item.versionNumber}</span><br /><span>{item.status.replace('-', ' ')}</span></>}
                        primaryTypographyProps={{ fontSize: 12.5, fontWeight: 700 }}
                        secondaryTypographyProps={{ fontSize: 10.5, textTransform: 'capitalize' }}
                      />
                    </ListItemButton>
                  ))}
                  {!filteredDocuments.length && <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>{documents.length ? 'No drafts match this search.' : 'No drafts yet. Create the first document for this matter.'}</Typography>}
                </List>
              </>}
              {leftTab === 1 && <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, py: 1.2 }}>Jump directly to a document section.</Typography>
                <List dense disablePadding>
                  {outline.map(item => (
                    <ListItemButton key={item.id} onClick={() => { item.node.scrollIntoView({ behavior: 'smooth', block: 'center' }); if (compactLayout) setLeftOpen(false); }} sx={{ pl: 1 + item.level, borderRadius: 1.5 }}>
                      <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 12, noWrap: true, fontWeight: item.level === 1 ? 750 : 500 }} />
                    </ListItemButton>
                  ))}
                  {!outline.length && <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>Add headings to create a navigable outline.</Typography>}
                </List>
              </>}
            </Box>
          </Paper>
          <Resizer side="left" onResize={setPanelWidth} />
        </>}

        <Box ref={editorCanvasRef} className="draft-canvas-scroll">
          {!document ? (
            <Paper sx={{ p: 5, maxWidth: 620, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={800}>Start a controlled legal document</Typography>
              <Typography color="text.secondary" sx={{ my: 2 }}>Choose a matter, then create a draft with court-format defaults, review gates, autosave, and immutable versions.</Typography>
              <Button variant="contained" startIcon={<AddRounded />} onClick={openNewDocumentDialog} disabled={!matter}>Create document</Button>
            </Paper>
          ) : (
            <Box className="draft-editor-stack">
              <Paper variant="outlined" className="draft-selectionbar">
                {selectionText ? <>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                    <Chip size="small" color="secondary" label={`${selectionWordCount} word${selectionWordCount === 1 ? '' : 's'} selected`} />
                    <Typography variant="caption" noWrap color="text.secondary" className="draft-selection-preview">{selectionText}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={.5}>
                    <Button size="small" variant="contained" startIcon={<AutoAwesomeRounded />} onClick={() => openInsights(0)}>Revise</Button>
                    <Button size="small" onClick={() => { setAiInstruction('Identify unsupported claims, missing facts, missing pinpoints, adverse authority, and verification requirements in this selection.'); openInsights(0); }}>Find gaps</Button>
                    <IconButton size="small" aria-label="Clear text selection" onClick={() => {
                      const range = savedRangeRef.current;
                      if (range) quillRef.current?.setSelection(range.index + range.length, 0, 'api');
                    }}><CloseRounded fontSize="small" /></IconButton>
                  </Stack>
                </> : <>
                  <Typography variant="caption" color="text.secondary">Select text to revise it with AI, then preview the proposed change before accepting it.</Typography>
                  <Button size="small" startIcon={<VerifiedRounded />} onClick={() => openInsights(1)}>Review readiness</Button>
                </>}
              </Paper>
              <Box className="draft-page-zoom" style={{ zoom: zoom / 100 }}>
                <Paper className="draft-page" elevation={3}>
                  <Box ref={editorHostRef} className="draft-quill" />
                </Paper>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>Live draft · {wordCount.toLocaleString()} words · autosaved privately</Typography>
              </Box>
            </Box>
          )}
        </Box>

        {rightOpen && <>
          <Resizer side="right" onResize={setPanelWidth} />
          <Paper square elevation={0} className="draft-right-panel" sx={{ width: rightWidth }}>
            <Tabs value={rightTab} onChange={(_, value) => setRightTab(value)} variant="scrollable" scrollButtons={false} sx={{ minHeight: 42 }}>
              <Tab label="AI" sx={{ minHeight: 42, minWidth: 58 }} />
              <Tab label="Review" sx={{ minHeight: 42, minWidth: 76 }} />
              <Tab label="Authorities" sx={{ minHeight: 42, minWidth: 92 }} />
              <Tab label="History" sx={{ minHeight: 42, minWidth: 70 }} />
            </Tabs>
            <Divider />
            <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
              {rightTab === 0 && <Stack spacing={1.5}>
                <Box>
                  <Typography fontWeight={800}>Revise the selection</Typography>
                  <Typography variant="caption" color="text.secondary">Select exact text in the document. Nothing changes until you accept the preview.</Typography>
                </Box>
                <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'grey.50', maxHeight: 120, overflow: 'auto' }}>
                  <Typography variant="caption" color="text.secondary">SELECTED TEXT</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectionText || 'No text selected.'}</Typography>
                </Paper>
                <Stack direction="row" flexWrap="wrap" gap={.6}>
                  {[
                    ['Tighten', 'Make this more concise and direct without changing meaning.'],
                    ['Formalize', 'Rewrite in precise, neutral court-ready prose.'],
                    ['Plain language', 'Make this clear to a client while preserving accuracy.'],
                    ['Find gaps', 'Identify unsupported claims and add explicit verification placeholders.'],
                  ].map(([label, instruction]) => <Chip key={label} label={label} size="small" clickable onClick={() => setAiInstruction(instruction)} />)}
                </Stack>
                <TextField label="Instruction" multiline minRows={3} value={aiInstruction} onChange={event => setAiInstruction(event.target.value)} />
                <FormControlLabel control={<Checkbox checked={aiConsent} onChange={event => setAiConsent(event.target.checked)} />} label={<Typography variant="caption">I confirm this selected matter content may be sent to the configured AI provider.</Typography>} />
                {!metadata?.capabilities?.aiConfigured && <Alert severity="warning">AI is unavailable until an OpenAI API key is configured. Editing, autosave, review, and version history still work.</Alert>}
                <Button variant="contained" startIcon={aiBusy ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeRounded />} disabled={aiBusy || !selectionText || !metadata?.capabilities?.aiConfigured} onClick={() => runAi('rewrite')}>Generate revision</Button>
                {aiResult && <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'primary.light' }}>
                  <Typography variant="overline" color="primary">Preview</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{aiResult.replacementText}</Typography>
                  <Typography variant="caption" color="text.secondary">{aiResult.rationale}</Typography>
                  {(aiResult.verificationItems || []).map((item, index) => <Alert key={index} severity={item.category === 'none' ? 'success' : 'warning'} sx={{ mt: 1, py: 0 }}>{item.item}</Alert>)}
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}><Button size="small" variant="contained" onClick={acceptAi}>Replace selection</Button><Button size="small" onClick={() => setAiResult(null)}>Discard</Button></Stack>
                </Paper>}
              </Stack>}

              {rightTab === 1 && document && <Stack spacing={1.5}>
                <Box>
                  <Stack direction="row" justifyContent="space-between"><Typography fontWeight={800}>Release readiness</Typography><Typography fontWeight={800}>{readiness}%</Typography></Stack>
                  <LinearProgress variant="determinate" value={readiness} sx={{ mt: .75, height: 8, borderRadius: 8 }} />
                </Box>
                <Alert severity={placeholderCount ? 'warning' : 'success'} icon={placeholderCount ? <WarningAmberRounded /> : <CheckCircleRounded />}>
                  {placeholderCount ? `${placeholderCount} drafting or verification placeholder${placeholderCount === 1 ? '' : 's'} remain.` : 'No bracketed verification placeholders detected.'}
                </Alert>
                <TextField select label="Workflow status" size="small" value={document.status} onChange={event => updateDocument({ status: event.target.value })}>
                  <MenuItem value="working">Working draft</MenuItem><MenuItem value="in-review">In review</MenuItem><MenuItem value="approved">Approved</MenuItem><MenuItem value="filed">Filed</MenuItem>
                </TextField>
                <Box>
                  <Typography variant="overline" color="text.secondary">Mandatory quality gate</Typography>
                  {qualityChecks.map(check => <FormControlLabel
                    key={check.key}
                    control={<Checkbox size="small" checked={document.checklist?.[check.key] === true} onChange={event => updateDocument({ checklist: { ...document.checklist, [check.key]: event.target.checked } })} />}
                    label={<Typography variant="body2">{check.label}</Typography>}
                    sx={{ display: 'flex', alignItems: 'flex-start', mb: .4 }}
                  />)}
                </Box>
                <Divider />
                <Typography variant="overline" color="text.secondary">Live document insights</Typography>
                <Stack direction="row" flexWrap="wrap" gap={.7}>
                  <Chip label={`${wordCount} words`} size="small" />
                  <Chip label={`${document.authorities?.length || 0} authorities`} size="small" />
                  <Chip label={`Version ${document.versionNumber}`} size="small" />
                </Stack>
                {!document.courtFileNumber && <Alert severity="warning">Court / Information number is missing.</Alert>}
                {document.metadata?.templateKind === 'official-form' && <Alert severity="info">
                  This is an official-form preparation workspace. Transfer the approved content into the current court-issued form before filing.
                  {document.metadata?.officialFormNumber ? ` Form: ${document.metadata.officialFormNumber}.` : ''}
                  {document.metadata?.officialSourceUrl && <Button size="small" href={document.metadata.officialSourceUrl} target="_blank" rel="noreferrer" sx={{ ml: .5 }}>Open official form source</Button>}
                </Alert>}
                {standard && <Paper variant="outlined" sx={{ p: 1.25 }}>
                  <Typography variant="subtitle2">{standard.name}</Typography>
                  {standard.rules.map(rule => <Typography key={rule} variant="caption" sx={{ display: 'block', mt: .65 }}>• {rule}</Typography>)}
                  <Button size="small" href={standard.sourceUrl} target="_blank" rel="noreferrer" sx={{ mt: 1 }}>Official direction</Button>
                </Paper>}
              </Stack>}

              {rightTab === 2 && document && <Stack spacing={1.25}>
                <Box><Typography fontWeight={800}>Authority register</Typography><Typography variant="caption" color="text.secondary">Citations are never treated as verified merely because AI supplied them.</Typography></Box>
                {(document.authorities || []).map((item, index) => <Paper key={`${item.citation}-${index}`} variant="outlined" sx={{ p: 1.25 }}>
                  <Typography variant="subtitle2">{item.title || item.citation}</Typography>
                  <Typography variant="caption" color="text.secondary">{[item.citation, item.pinpoint].filter(Boolean).join(' · ')}</Typography>
                  {item.proposition && <Typography variant="body2" sx={{ mt: .5 }}>{item.proposition}</Typography>}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: .75 }}>
                    {item.url ? <Button size="small" href={item.url} target="_blank" rel="noreferrer">Open source</Button> : <Typography variant="caption" color="error">Source URL missing</Typography>}
                    <FormControlLabel control={<Checkbox size="small" checked={item.verified === true} onChange={event => toggleAuthority(index, event.target.checked)} />} label={<Typography variant="caption">Verified</Typography>} />
                  </Stack>
                </Paper>)}
                <Divider><Typography variant="caption">ADD AUTHORITY</Typography></Divider>
                <TextField size="small" label="Case / source title" value={authority.title} onChange={event => setAuthority(value => ({ ...value, title: event.target.value }))} />
                <TextField size="small" label="Neutral citation" value={authority.citation} onChange={event => setAuthority(value => ({ ...value, citation: event.target.value }))} />
                <TextField size="small" label="Pinpoint paragraph / page" value={authority.pinpoint} onChange={event => setAuthority(value => ({ ...value, pinpoint: event.target.value }))} />
                <TextField size="small" label="Authoritative source URL" value={authority.url} onChange={event => setAuthority(value => ({ ...value, url: event.target.value }))} />
                <TextField size="small" multiline minRows={2} label="Proposition supported" value={authority.proposition} onChange={event => setAuthority(value => ({ ...value, proposition: event.target.value }))} />
                <TextField size="small" label="Treatment / noted-up result" value={authority.treatment} onChange={event => setAuthority(value => ({ ...value, treatment: event.target.value }))} />
                <Button variant="outlined" onClick={addAuthority}>Add unverified authority</Button>
              </Stack>}

              {rightTab === 3 && document && <Stack spacing={1.25}>
                <Box><Typography fontWeight={800}>Immutable version history</Typography><Typography variant="caption" color="text.secondary">Autosave updates the live draft. Checkpoints are permanent, checksum-backed snapshots.</Typography></Box>
                <Button variant="contained" startIcon={<SaveRounded />} onClick={createVersion}>Save named checkpoint</Button>
                {(document.versions || []).map(version => <Paper key={version.id} variant="outlined" sx={{ p: 1.25 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box><Typography variant="subtitle2">v{version.versionNumber} · {version.label}</Typography><Typography variant="caption" color="text.secondary">{new Date(version.createdAt).toLocaleString()} · {version.createdBy || 'LexMaple'}</Typography></Box>
                    <Stack direction="row" spacing={.4}>
                      <Button size="small" startIcon={<VisibilityRounded />} onClick={() => setVersionPreview(version)}>View</Button>
                      <Button size="small" onClick={() => restoreVersion(version)} disabled={version.versionNumber === document.versionNumber}>Restore</Button>
                    </Stack>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>SHA-256 {version.contentHash.slice(0, 12)}…</Typography>
                </Paper>)}
              </Stack>}
            </Box>
          </Paper>
        </>}
      </Box>

      <Box className="draft-statusbar">
        <Typography variant="caption">{matter?.matterNumber || 'No matter'} · {document?.documentType || 'No document'}</Typography>
        <Stack direction="row" spacing={1.5}><Typography variant="caption">{wordCount.toLocaleString()} words</Typography><Typography variant="caption">{zoom}%</Typography><Typography variant="caption">{saveState === 'saved' ? 'Saved privately' : saveState === 'saving' ? 'Saving privately' : 'Private workspace'}</Typography></Stack>
      </Box>

      <Dialog open={checkpointDialog} onClose={() => setCheckpointDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Save a named checkpoint</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: .5 }}>
            <Typography variant="body2" color="text.secondary">Create an immutable snapshot before review, service, filing, or a substantial revision. The live draft remains editable.</Typography>
            <TextField label="Checkpoint label" value={checkpointLabel} onChange={event => setCheckpointLabel(event.target.value)} autoFocus fullWidth placeholder="e.g. Client-approved facts · 10 Oct 2026" />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
              <Paper variant="outlined" sx={{ p: 1.2 }}><Typography fontWeight={800}>{wordCount}</Typography><Typography variant="caption" color="text.secondary">Words</Typography></Paper>
              <Paper variant="outlined" sx={{ p: 1.2 }}><Typography fontWeight={800}>{readiness}%</Typography><Typography variant="caption" color="text.secondary">Readiness</Typography></Paper>
              <Paper variant="outlined" sx={{ p: 1.2 }}><Typography fontWeight={800}>{placeholderCount}</Typography><Typography variant="caption" color="text.secondary">Placeholders</Typography></Paper>
            </Box>
            {saveState !== 'saved' && <Alert severity="info">The current live edits will be saved before this checkpoint is created.</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setCheckpointDialog(false)}>Cancel</Button><Button variant="contained" onClick={saveCheckpoint} disabled={!checkpointLabel.trim()}>Save checkpoint</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(versionPreview)} onClose={() => setVersionPreview(null)} fullWidth maxWidth="lg">
        <DialogTitle>
          Version {versionPreview?.versionNumber} · {versionPreview?.label}
          <Typography variant="body2" color="text.secondary">Read-only snapshot · {versionPreview?.createdAt ? new Date(versionPreview.createdAt).toLocaleString() : ''}</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#e5e9ee' }}>
          <Paper className="draft-version-preview" dangerouslySetInnerHTML={{ __html: versionPreview?.contentHtml || '<p>No snapshot content available.</p>' }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionPreview(null)}>Close</Button>
          <Button variant="contained" onClick={() => restoreVersion(versionPreview)} disabled={!versionPreview || versionPreview.versionNumber === document?.versionNumber}>Restore as new version</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={newDialog} onClose={() => setNewDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>
          Create a lifecycle document
          <Typography variant="body2" color="text.secondary">{matter?.jurisdiction || 'Jurisdiction not set'} · {matter?.court || 'Court not set'} · catalog {metadata?.catalogVersion}</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: .5 }}>
            {!matterCourtId && <Alert severity="warning">Set a supported Ontario or British Columbia criminal court on the matter to receive court-specific forms.</Alert>}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.25fr 1fr 1fr' }, gap: 1.5 }}>
              <TextField select label="Lifecycle stage" value={newStage} onChange={event => { setNewStage(event.target.value); setNewTitle(''); }}>
                {(metadata?.lifecycleStages || []).map(stage => <MenuItem key={stage.id} value={stage.id}>{stage.title}</MenuItem>)}
              </TextField>
              <TextField select label="Proceeding" value={newProceedingType} onChange={event => setNewProceedingType(event.target.value)}>
                <MenuItem value="">All criminal matters</MenuItem>
                {(metadata?.proceedingTypes || []).map(type => <MenuItem key={type.id} value={type.id}>{type.label}</MenuItem>)}
              </TextField>
              <TextField select label="Document source" value={newKind} onChange={event => setNewKind(event.target.value)}>
                <MenuItem value="">Official forms + work product</MenuItem>
                <MenuItem value="official-form">Official court forms</MenuItem>
                <MenuItem value="work-product">Practice work product</MenuItem>
              </TextField>
            </Box>
            <TextField label="Find by name, category or form number" value={newQuery} onChange={event => setNewQuery(event.target.value)} placeholder="e.g. bail review, PCR315, factum" />
            <TextField
              select
              label={`Document template · ${catalogTemplates.length} available`}
              value={newTemplateId}
              onChange={event => { setNewTemplateId(event.target.value); setNewTitle(''); }}
              disabled={!catalogTemplates.length}
            >
              {catalogTemplates.map(template => <MenuItem key={template.id} value={template.id}>
                {template.title}{template.officialFormNumber ? ` · ${template.officialFormNumber}` : ''}{template.kind === 'official-form' ? ' · Official' : ''}
              </MenuItem>)}
            </TextField>
            {!catalogTemplates.length && <Alert severity="warning">No document matches these filters. Broaden the proceeding or source filter, or correct the matter court.</Alert>}
            {newTemplate && <Paper variant="outlined" sx={{ p: 1.75 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
                <Box>
                  <Stack direction="row" gap={.75} flexWrap="wrap" sx={{ mb: .75 }}>
                    <Chip size="small" label={newTemplate.kind === 'official-form' ? 'Official court form' : 'Practice work product'} color={newTemplate.kind === 'official-form' ? 'primary' : 'default'} />
                    {newTemplate.category !== 'Official court form' && <Chip size="small" label={newTemplate.category} variant="outlined" />}
                    {newTemplate.officialFormNumber && <Chip size="small" label={newTemplate.officialFormNumber} variant="outlined" />}
                  </Stack>
                  <Typography fontWeight={800}>{newTemplate.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>{newTemplate.description}</Typography>
                </Box>
                <Button href={newTemplate.source?.url} target="_blank" rel="noreferrer" sx={{ alignSelf: 'flex-start', whiteSpace: 'nowrap' }}>Official source</Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>{newTemplate.instructions}</Typography>
            </Paper>}
            <TextField label="Document title" value={newTitle} onChange={event => setNewTitle(event.target.value)} placeholder={`${newTemplate?.title || 'Document'} — ${matter?.clientName || matter?.title || ''}`} />
            <Alert severity="info">Every draft starts with Ontario or BC matter/court metadata, a mandatory verification gate, autosave and an immutable initial version. The responsible professional must verify scope, deadlines, governing rules, and the triggering event.</Alert>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setNewDialog(false)}>Cancel</Button><Button variant="contained" onClick={createDocument} disabled={!newTemplate}>Create controlled draft</Button></DialogActions>
      </Dialog>

      {(error || notice) && <Box className="draft-toast"><Alert severity={error ? 'error' : 'success'} onClose={() => { setError(''); setNotice(''); }}>{error || notice}</Alert></Box>}
    </Box>
  );
}
